import { CloseOutlined } from '@ant-design/icons';
import { Button, Tabs, List, Typography } from 'antd';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MARK_AS_VIEWED_DELAY, POLLING_INTERVAL_ACTIVE, POLLING_INTERVAL_BACKGROUND, POLLING_INTERVAL_NIGHT } from '../../constants';
import { INewsChatViewDTO } from '../../libs/coreapi-dto/service/newsChat';
import { UISettingsManager } from '../../libs/ui-settings-manager/UISettingsManager';
import { NewsChatDataProvider } from '../../Services/DataProviders/NewsChatDataProvider';
import { playAlertSound } from '../../system/functions/beepSound';
import { useTimeFormatter } from '../../system/hooks/useTimeFormatter';
import { useAppContext } from '../../system/providers/appContextProvider';
import { useNews } from '../../system/providers/newsContext';
import { useUserContext } from '../../system/providers/userContextProvider';
import ChatSettingsButton from './components/chatSettingsButton/ChatSettingsButton';
import FloatingChatButton from './components/floatingChatButton/FloatingChatButton';
import styles from './NewsChatModal.module.scss'

enum RedmineNewsPriority { Low = 1, Medium = 2, High = 3, Critical = 4 }

const PRIORITY_STYLES = {
    [RedmineNewsPriority.Low]: styles.priorityLow,
    [RedmineNewsPriority.Medium]: styles.priorityMedium,
    [RedmineNewsPriority.High]: styles.priorityHigh,
    [RedmineNewsPriority.Critical]: styles.priorityCritical,
};

const NewsChatModal = () => {
    const appContext = useAppContext();
    const userContext = useUserContext();
    const newsChatDataProvider = new NewsChatDataProvider(appContext.coreApiService);

    const { 
        news: contextNews, 
        unreadCount: contextUnreadCount,
        updateNews,
        markAsViewed: contextMarkAsViewed,
        isLoading: contextIsLoading,
        setIsLoading: setContextIsLoading
    } = useNews();
    
    const [open, setOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('news');
    const [isPolling, setIsPolling] = useState(true);
    const pollingRef = useRef<NodeJS.Timeout>();
    const isActiveTabRef = useRef(true);
    const [settings, setSettings] = useState(() => UISettingsManager.getSettings().chat);
    const viewTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
    const { formatEstimatedHour } = useTimeFormatter();
    const lastUnreadCountRef = useRef<number>(0);
    const isSuperUser = userContext.userRoles.includes('SuperUser');
    const isGlobalAdmin = userContext.userRoles
            .flat(Infinity)
            .filter(role => typeof role === 'string')
            .map(role => role.toLowerCase())    
            .includes('globaladmin');

    // Refs для прокрутки
    const listRef = useRef<HTMLDivElement>(null);
    const newsItemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const pendingScrollToNewsId = useRef<string | null>(null);

    const { t } = useTranslation();
    const baseT = (value: string) => t('chat.' + value);

    // Сохраняем предыдущее количество непрочитанных для отслеживания новых
    useEffect(() => {
        lastUnreadCountRef.current = contextUnreadCount;
    }, [contextUnreadCount]);

    const getModalPosition = () => {
        if (settings.position === 'top') {
            return {
                top: 65,
                right: 35,
                bottom: 'auto',
            };
        }
        
        return {
            bottom: 145,
            right: 35,
            top: 'auto',
        };
    };

    const fetchNews = useCallback(async () => {
        try {
            if (!isActiveTabRef.current || isSuperUser || isGlobalAdmin) return;

            setContextIsLoading(true);
            newsChatDataProvider.getNews((entities) => {
                 // Подсчитываем новые непрочитанные новости
                 const newUnreadEntities = entities.filter(n => !n.isViewed);
                 const currentUnreadIds = new Set(
                     contextNews.filter(n => !n.isViewed).map(n => n.idGlobal)
                 );
                 const brandNewUnread = newUnreadEntities.filter(
                     n => !currentUnreadIds.has(n.idGlobal)
                 );
                 
                 // Если есть новые новости и звук включен - воспроизводим звук
                 if (brandNewUnread.length > 0 && settings.sound === true && !open) {
                     playAlertSound('info');
                 }
                 
                 // Обновляем новости в контексте (синхронизация с localStorage)
                 updateNews(entities);
                 setContextIsLoading(false);
            });
        } catch (error) {
            console.error(error);
            setContextIsLoading(false);
        }
    }, [newsChatDataProvider, updateNews, setContextIsLoading, contextNews, settings.sound, open]);

    // Инициализация: загружаем новости при монтировании
    useEffect(() => {
        fetchNews();
    }, []);

    useEffect(() => {
        if (!isPolling) return;

        const getCurrentInterval = () => {
            if (open) return POLLING_INTERVAL_ACTIVE;
            
            const hours = new Date().getHours();
            if (hours >= 23 || hours < 6) return POLLING_INTERVAL_NIGHT;
            
            return POLLING_INTERVAL_BACKGROUND;
        };

        const startPolling = () => {
            if (pollingRef.current) {
                clearTimeout(pollingRef.current);
            }

            const interval = getCurrentInterval();
            
            pollingRef.current = setTimeout(() => {
                fetchNews();
                startPolling(); // Рекурсивный вызов для следующего опроса
            }, interval);
        };

        startPolling();

        return () => {
            if (pollingRef.current) {
                clearTimeout(pollingRef.current);
            }
        };
    }, [open, isPolling]);

    useEffect(() => {
        const handleVisibilityChange = () => {
            isActiveTabRef.current = !document.hidden;
            
            // Если вкладка неактивна (пользователь переключился)
            if (document.hidden) {
                // Паузим polling на время неактивности
                setIsPolling(false);
            } else {
                // Вкладка снова активна - возобновляем polling
                setIsPolling(true);
                fetchNews();
            }
        };
    
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, []);
    
    useEffect(() => {
        const handleSettingsChange = () => {
            const uiSettings = UISettingsManager.getSettings();
            setSettings(uiSettings.chat);
        };

        window.addEventListener('uiSettingsChanged', handleSettingsChange);
        return () => window.removeEventListener('uiSettingsChanged', handleSettingsChange);
    }, []);

    const handleMarkAsViewed = useCallback(async (idGlobal: string) => {
        try {
            await newsChatDataProvider.markNewAsViewed(idGlobal);
            contextMarkAsViewed(idGlobal);
            
            if (viewTimersRef.current.has(idGlobal)) {
                clearTimeout(viewTimersRef.current.get(idGlobal));
                viewTimersRef.current.delete(idGlobal);
            }
        } catch (e) { 
            console.error(e); 
        }
    }, [newsChatDataProvider, contextMarkAsViewed]);

    const handleNewsClick = useCallback((idGlobal: string, isViewed: boolean) => {
        if (isViewed) return;

        if (viewTimersRef.current.has(idGlobal)) {
            clearTimeout(viewTimersRef.current.get(idGlobal));
            viewTimersRef.current.delete(idGlobal);
            return;
        }
        
        const timer = setTimeout(() => {
            handleMarkAsViewed(idGlobal);
        }, MARK_AS_VIEWED_DELAY);
        
        viewTimersRef.current.set(idGlobal, timer);
    }, [handleMarkAsViewed]);

    const scrollToNews = useCallback((newsId: string) => {
        const newsElement = newsItemRefs.current.get(newsId);
        if (newsElement && listRef.current) {
            // Плавная прокрутка к элементу
            newsElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
            
            // Добавляем временный класс для подсветки
            newsElement.classList.add(styles.highlighted);
            setTimeout(() => {
                newsElement.classList.remove(styles.highlighted);
            }, 2000);
        }
    }, []);

    // Очистка всех таймеров при размонтировании
    useEffect(() => {
        return () => {
            viewTimersRef.current.forEach(timer => clearTimeout(timer));
            viewTimersRef.current.clear();
        };
    }, []);

    // глобальный обработчик для открытия чата (напр. из карусели)
    useEffect(() => {
        const handleOpenNewsChat = (event: CustomEvent) => {
            setOpen(true);
            // Если нужно открыть конкретную новость
            if (event.detail?.newsId) {
                const newsId = event.detail.newsId;
                
                // Проверяем, загружены ли уже новости
                const newsExists = contextNews.some(n => n.idGlobal === newsId);
                
                if (newsExists) {
                    // Если новость уже в списке, прокручиваем к ней
                    setTimeout(() => {
                        scrollToNews(newsId);
                    }, 300); // Задержка для открытия анимации модального окна
                } else {
                    // Если новость еще не загружена, запоминаем ID для прокрутки после загрузки
                    pendingScrollToNewsId.current = newsId;
                }
            }
        };
    
        window.addEventListener('openNewsChat', handleOpenNewsChat as EventListener);
        return () => {
            window.removeEventListener('openNewsChat', handleOpenNewsChat as EventListener);
        };
    }, [contextNews, scrollToNews]);

    // Прокрутка к сохраненной новости после загрузки данных
    useEffect(() => {
        if (pendingScrollToNewsId.current && contextNews.length > 0 && open) {
            const newsId = pendingScrollToNewsId.current;
            const newsExists = contextNews.some(n => n.idGlobal === newsId);
            
            if (newsExists) {
                setTimeout(() => {
                    scrollToNews(newsId);
                    pendingScrollToNewsId.current = null;
                }, 300);
            }
        }
    }, [contextNews, open, scrollToNews]);

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
    }, []);

    const renderNewsContent = () => {
        if (contextIsLoading && contextNews.length === 0) {
            return (
                <div style={{ padding: 24, textAlign: 'center', color: 'rgba(0,0,0,0.45)' }}>
                    {baseT('loading')}
                </div>
            );
        }
        if (contextNews.length === 0) {
            return (
                <div style={{ padding: 24, textAlign: 'center', color: 'rgba(0,0,0,0.45)' }}>
                    {baseT('noNews')}
                </div>
            );
        }

        return (
            <div ref={listRef} className={styles.newsListContainer}>
                <List<INewsChatViewDTO>
                    dataSource={contextNews}
                    renderItem={item => {
                        return (
                            <div
                                key={item.idGlobal}
                                ref={(el) => {
                                    if (el) {
                                        newsItemRefs.current.set(item.idGlobal, el);
                                    } else {
                                        newsItemRefs.current.delete(item.idGlobal);
                                    }
                                }}
                            >
                                <List.Item
                                    key={item.idGlobal}
                                    style={{ cursor: item.isViewed ? 'not-allowed' : 'pointer' }}
                                    className={`${styles.newsItem} ${item.isViewed ? styles.viewed : ''}`}
                                    onClick={() => handleNewsClick(item.idGlobal, item.isViewed)}
                                >
                                    <div className={`${styles.newsItemStrip} ${PRIORITY_STYLES[item.priority]}`} />

                                    <List.Item.Meta
                                        title={
                                            <Typography.Text
                                                strong
                                            >
                                                <div className={styles.itemStartDate}>
                                                    {item.startDate ? new Date(item.startDate).toLocaleDateString('ru-RU', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: '2-digit'
                                                    }) : new Date().toLocaleDateString('ru-RU')}
                                                </div>
                                                {item.subject}
                                            </Typography.Text>
                                        }
                                        description={
                                            <div>
                                                <Typography.Text type="secondary">
                                                    {item.description}
                                                </Typography.Text>
                                                {item.estimatedHour > 0 && (
                                                    <Typography.Text
                                                        type="secondary"
                                                        style={{
                                                            fontSize: '11px',
                                                            display: 'block',
                                                            marginTop: '8px',
                                                            color: '#adb5bd'
                                                        }}
                                                    >
                                                        ⏱ {formatEstimatedHour(item.estimatedHour)}
                                                    </Typography.Text>
                                                )}
                                            </div>
                                        }
                                    />
                                </List.Item>
                            </div>
                        )
                    }}
                />
            </div>
        );
    };

    const newsTabContent = <div>{renderNewsContent()}</div>;

    const items = [
        { label: baseT('newsTitle'), key: 'news', children: newsTabContent },
    ];

    if (isSuperUser || isGlobalAdmin) return <></>

    return (
        <>
            <FloatingChatButton 
                unreadCount={contextUnreadCount}
                onClick={open ? handleClose : handleOpen}
                isOpen={open}
            />
            <div
                className={`${styles.modalWindow} ${open ? styles.visible : ''}`}
                style={{
                ...(open && { opacity: settings.opacity }),
                ...getModalPosition()
                }}
            >
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={items}
                    type="card"
                    tabBarExtraContent={{
                        right: (
                            <>
                                <ChatSettingsButton />
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={handleClose}
                                    size="small"
                                    style={{ marginRight: 8 }}
                                />
                            </>
                        )
                    }}
                />
            </div>
        </>
    );
};

export default NewsChatModal;